import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import {
  Building2,
  X,
  ChevronRight,
  ChevronLeft,
  Check,
  MapPin,
  Home,
  DollarSign,
  Settings,
  CreditCard,
  Hash,
  Layers,
  Clock,
  Edit,
  Plus,
  AlertCircle,
  Zap,
  Trash2,
} from "lucide-react";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { useToast } from "../ui/Toaster";
import { UnitTypesSection } from "../forms/UnitTypesSection";
import { useAuth } from "../../contexts/AuthContext";
import { markSettingsReturnToAddProperty } from "../../utils/settingsReturnFlow";
import {
  payheroChannelService,
  PayHeroChannel,
} from "../../services/payheroChannelService";
import {
  organizationService,
  PropertyOwner,
} from "../../services/organization.service";
import { apiService } from "../../services/api";
import AddressLocationPicker, {
  AddressSelection,
} from "../properties/AddressLocationPicker";
import LateFeePolicyManager from "../property/LateFeePolicyManager";

// ─── Types ────────────────────────────────────────────────────────────────────
interface UnitTypeConfig {
  id: string;
  type: string;
  count: string;
  houseNumbers: string[];
  billingMethod?: "RATE_PER_UNIT" | "LUMP_SUM_DISTRIBUTION";
  ratePerUnit?: string;
  rent: string;
  deposit: string;
  size?: string;
  bedrooms?: string;
  bathrooms?: string;
}

interface SharedUtilityGroupConfig {
  id: string;
  utilityType: string;
  groupName: string;
  houseNumbers: string[];
  billingMethod?: "RATE_PER_UNIT" | "LUMP_SUM_DISTRIBUTION";
  ratePerUnit?: string;
}

interface IndividualUtilityMappingConfig {
  id: string;
  utilityType: string;
  unitType: string;
  houseNumbers: string[];
  billingMethod?: "RATE_PER_UNIT" | "LUMP_SUM_DISTRIBUTION";
  ratePerUnit?: string;
}

interface AdditionalUtilityChargeConfig {
  type: "TRASH" | "INTERNET" | "SEWERAGE" | "SECURITY" | "OTHER";
  enabled: boolean;
  amount: string;
}

interface UnitNamingGroup {
  id: string;
  prefix: string;
  start: string;
  end: string;
}

function parseUnitNamingGroupsFromProperty(raw: unknown): UnitNamingGroup[] {
  if (!raw || !Array.isArray(raw)) {
    return [{ id: "grp-1", prefix: "A", start: "1", end: "10" }];
  }
  return raw.map((g: any, i: number) => ({
    id: `grp-${i}-${String(g.prefix ?? "")}-${g.start}-${g.end}`,
    prefix: String(g.prefix ?? ""),
    start: String(g.start ?? ""),
    end: String(g.end ?? ""),
  }));
}

interface PropertyFormData {
  name: string;
  location: string;
  address: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  county?: string;
  country?: string;
  postalCode?: string;
  latitude?: number | null;
  longitude?: number | null;
  placeId?: string;
  geocodeProvider?: string;
  geocodeConfidence?: number | null;
  ownerName?: string;
  ownerPhone?: string;
  ownerEmail?: string;
  ownerNotes?: string;
  propertyOwnerId?: string;
  type: string;
  totalUnits: number;
  unitNamingConvention: string;
  customNamingPattern?: string;
  unitTypeConfigs?: UnitTypeConfig[];
  allowPartialPayments?: boolean;
  allow2Installments?: boolean;
  allow3Installments?: boolean;
  allow4Installments?: boolean;
  enableLateFees?: boolean;
  payheroChannelId?: string;
  utilityBillingMode?: "SHARED" | "INDIVIDUAL";
  sharedUtilityGroups?: SharedUtilityGroupConfig[];
  individualUtilityMappings?: IndividualUtilityMappingConfig[];
  additionalUtilityCharges?: AdditionalUtilityChargeConfig[];
  defaultAgreementContent?: string;
  unitNamingGroups?: UnitNamingGroup[];
}

interface FormErrors {
  [key: string]: string | undefined;
}

export interface AddPropertyDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: PropertyFormData) => void;
  property?: any;
  isLoading?: boolean;
  onLocationPatched?: (property: any) => void;
}

// ─── Animations ───────────────────────────────────────────────────────────────
const slideIn = keyframes`
  from { transform: translateX(100%); opacity: 0.6; }
  to   { transform: translateX(0);    opacity: 1; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

// ─── Overlay & Drawer ─────────────────────────────────────────────────────────
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.48);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: flex-end;
  animation: ${fadeIn} 0.2s ease;
`;

const Drawer = styled.div`
  position: relative;
  width: min(660px, 100vw);
  height: 100%;
  background: ${(p) => p.theme.background};
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: -12px 0 48px rgba(0, 0, 0, 0.2);
  overflow: hidden;

  @media (max-width: 660px) {
    width: 100vw;
  }
`;

// ─── Header ───────────────────────────────────────────────────────────────────
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px 14px;
  border-bottom: 1px solid ${(p) => p.theme.border};
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconBadge = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${(p) => `${p.theme.primary}18`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => p.theme.primary};
  flex-shrink: 0;
`;

const TitleGroup = styled.div``;

const DrawerTitle = styled.h2`
  font-size: 1.0625rem;
  font-weight: 700;
  color: ${(p) => p.theme.foreground};
  margin: 0 0 2px;
  line-height: 1.3;
`;

const DrawerSub = styled.p`
  font-size: 0.72rem;
  color: ${(p) => p.theme.mutedForeground};
  margin: 0;
`;

const CloseBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid ${(p) => p.theme.border};
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${(p) => p.theme.mutedForeground};
  transition: all 0.15s;
  flex-shrink: 0;

  &:hover {
    background: ${(p) => p.theme.muted};
    color: ${(p) => p.theme.foreground};
  }
`;

// ─── Step Progress ────────────────────────────────────────────────────────────
const StepsRow = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 24px;
  gap: 0;
  border-bottom: 1px solid ${(p) => p.theme.border};
  flex-shrink: 0;
  background: ${(p) => p.theme.muted};
`;

const StepItem = styled.div<{ $clickable: boolean }>`
  display: flex;
  align-items: center;
  gap: 7px;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
`;

const StepDot = styled.div<{ $state: "done" | "active" | "idle" }>`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  flex-shrink: 0;
  transition: all 0.25s;

  ${(p) =>
    p.$state === "done" &&
    `
    background: ${p.theme.primary};
    color: ${p.theme.primaryForeground};
  `}
  ${(p) =>
    p.$state === "active" &&
    `
    background: ${p.theme.primary};
    color: ${p.theme.primaryForeground};
    box-shadow: 0 0 0 3px ${p.theme.primary}33;
  `}
  ${(p) =>
    p.$state === "idle" &&
    `
    background: ${p.theme.muted};
    color: ${p.theme.mutedForeground};
  `}
`;

const StepName = styled.span<{ $state: "done" | "active" | "idle" }>`
  font-size: 0.72rem;
  font-weight: ${(p) => (p.$state === "active" ? "600" : "400")};
  color: ${(p) =>
    p.$state === "idle" ? p.theme.mutedForeground : p.theme.foreground};
  display: none;

  @media (min-width: 380px) {
    display: inline;
  }
`;

const StepLine = styled.div<{ $done: boolean }>`
  flex: 1;
  height: 2px;
  background: ${(p) => (p.$done ? p.theme.primary : p.theme.border)};
  margin: 0 6px;
  max-width: 40px;
  transition: background 0.3s;
`;

// ─── Body ─────────────────────────────────────────────────────────────────────
const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 22px 24px;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${(p) => p.theme.border};
    border-radius: 4px;
  }
`;

// ─── Form Sections ────────────────────────────────────────────────────────────
const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.72rem;
  font-weight: 700;
  color: ${(p) => p.theme.mutedForeground};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 14px;

  svg {
    color: ${(p) => p.theme.primary};
  }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 380px) {
    grid-template-columns: 1fr;
  }
`;

const FullCol = styled.div`
  margin-bottom: 12px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FieldLbl = styled.label`
  font-size: 0.8rem;
  font-weight: 500;
  color: ${(p) => p.theme.foreground};
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    color: ${(p) => p.theme.mutedForeground};
  }

  .req {
    color: ${(p) => p.theme.destructive};
    margin-left: 1px;
  }
  .opt {
    font-size: 0.69rem;
    font-weight: 400;
    color: ${(p) => p.theme.mutedForeground};
    margin-left: 3px;
  }
`;

const ErrMsg = styled.div`
  font-size: 0.7rem;
  color: ${(p) => p.theme.destructive};
  display: flex;
  align-items: center;
  gap: 3px;
`;

// Property type selector cards
const TypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
`;

const TypeCard = styled.button<{ $active: boolean }>`
  padding: 12px 6px;
  border-radius: 10px;
  border: none;
  background: ${(p) =>
    p.$active ? `${p.theme.primary}14` : p.theme.card};
  cursor: pointer;
  text-align: center;
  transition: box-shadow 0.18s ease, background 0.18s ease;
  box-shadow: ${(p) =>
    p.$active
      ? `0 4px 18px -6px color-mix(in oklch, ${p.theme.foreground} 18%, transparent), 0 2px 10px -4px color-mix(in oklch, ${p.theme.primary} 40%, transparent)`
      : `0 1px 4px color-mix(in oklch, ${p.theme.foreground} 10%, transparent)`};

  .type-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 6px;
    color: ${(p) =>
      p.$active ? p.theme.primary : p.theme.mutedForeground};
  }
  .type-label {
    font-size: 0.69rem;
    font-weight: ${(p) => (p.$active ? "700" : "500")};
    color: ${(p) =>
      p.$active ? p.theme.primary : p.theme.mutedForeground};
  }

  &:hover {
    background: ${(p) => `${p.theme.primary}0c`};
    box-shadow: ${(p) =>
      p.$active
        ? `0 6px 22px -6px color-mix(in oklch, ${p.theme.foreground} 20%, transparent), 0 2px 12px -4px color-mix(in oklch, ${p.theme.primary} 45%, transparent)`
        : `0 3px 10px color-mix(in oklch, ${p.theme.foreground} 12%, transparent)`};
  }
`;

// Naming convention cards
const NamingGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 14px;
`;

const NamingCard = styled.button<{ $active: boolean }>`
  padding: 13px 12px;
  border-radius: 10px;
  border: none;
  background: ${(p) =>
    p.$active ? `${p.theme.accent}16` : p.theme.card};
  cursor: pointer;
  text-align: left;
  transition: box-shadow 0.18s ease, background 0.18s ease;
  box-shadow: ${(p) =>
    p.$active
      ? `0 4px 16px -6px color-mix(in oklch, ${p.theme.foreground} 16%, transparent), 0 2px 8px -4px color-mix(in oklch, ${p.theme.accent} 35%, transparent)`
      : `0 1px 4px color-mix(in oklch, ${p.theme.foreground} 10%, transparent)`};

  .nm-title {
    font-size: 0.8rem;
    font-weight: 600;
    color: ${(p) =>
      p.$active ? p.theme.accentForeground : p.theme.foreground};
    margin-bottom: 3px;
  }
  .nm-example {
    font-size: 0.68rem;
    color: ${(p) => p.theme.mutedForeground};
    font-family: ${(p) => p.theme.fontMono};
  }

  &:hover {
    background: ${(p) => `${p.theme.accent}0e`};
    box-shadow: ${(p) =>
      `0 4px 14px -4px color-mix(in oklch, ${p.theme.foreground} 14%, transparent)`};
  }
`;

// Toggle row
const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 15px;
  border-radius: 10px;
  border: 1px solid ${(p) => p.theme.border};
  background: ${(p) => p.theme.muted}18;
  margin-bottom: 10px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;

  &:hover {
    background: ${(p) => p.theme.muted}38;
  }
`;

const ToggleInfo = styled.div`
  .tg-title {
    font-size: 0.8375rem;
    font-weight: 500;
    color: ${(p) => p.theme.foreground};
  }
  .tg-desc {
    font-size: 0.69rem;
    color: ${(p) => p.theme.mutedForeground};
    margin-top: 1px;
  }
`;

const ToggleSwitch = styled.div<{ $on: boolean }>`
  width: 38px;
  height: 21px;
  border-radius: 11px;
  background: ${(p) => (p.$on ? p.theme.primary : p.theme.muted)};
  position: relative;
  transition: background 0.22s;
  flex-shrink: 0;
  margin-left: 12px;

  &::after {
    content: "";
    position: absolute;
    width: 17px;
    height: 17px;
    border-radius: 50%;
    background: ${(p) => p.theme.card};
    top: 2px;
    left: ${(p) => (p.$on ? "19px" : "2px")};
    transition: left 0.22s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.22);
  }
`;

// Installment pills
const InstallmentPills = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid ${(p) => p.theme.border};
  background: ${(p) => p.theme.muted}10;
  margin-bottom: 10px;
`;

const Pill = styled.button<{ $on: boolean }>`
  padding: 5px 14px;
  border-radius: 20px;
  border: none;
  background: ${(p) =>
    p.$on ? `${p.theme.primary}18` : `${p.theme.muted}30`};
  color: ${(p) =>
    p.$on ? p.theme.primary : p.theme.mutedForeground};
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: box-shadow 0.18s ease, background 0.18s ease, color 0.15s;
  box-shadow: ${(p) =>
    p.$on
      ? `0 3px 14px -4px color-mix(in oklch, ${p.theme.foreground} 14%, transparent), 0 2px 10px -4px color-mix(in oklch, ${p.theme.primary} 38%, transparent)`
      : `0 1px 3px color-mix(in oklch, ${p.theme.foreground} 8%, transparent)`};

  &:hover {
    background: ${(p) => (p.$on ? `${p.theme.primary}22` : `${p.theme.primary}0c`)};
    color: ${(p) => (p.$on ? p.theme.primary : p.theme.primary)};
    box-shadow: ${(p) =>
      p.$on
        ? `0 4px 18px -4px color-mix(in oklch, ${p.theme.foreground} 16%, transparent), 0 2px 12px -4px color-mix(in oklch, ${p.theme.primary} 42%, transparent)`
        : `0 2px 8px color-mix(in oklch, ${p.theme.foreground} 10%, transparent)`};
  }
`;

// Unit preview
const PreviewBox = styled.div`
  background: ${(p) => p.theme.muted}18;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 10px;
  padding: 13px;
  margin-top: 12px;
`;

const PreviewTitle = styled.div`
  font-size: 0.72rem;
  font-weight: 500;
  color: ${(p) => p.theme.mutedForeground};
  margin-bottom: 8px;
`;

const PreviewTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

const PreviewTag = styled.span`
  background: ${(p) => p.theme.background};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 5px;
  padding: 2px 7px;
  font-size: 0.72rem;
  font-family: monospace;
  color: ${(p) => p.theme.foreground};
`;

const PreviewMore = styled.span`
  font-size: 0.72rem;
  color: ${(p) => p.theme.mutedForeground};
  font-style: italic;
  align-self: center;
`;

const Divider = styled.div`
  height: 1px;
  background: ${(p) => p.theme.border};
  margin: 18px 0;
`;

const InfoNote = styled.div`
  font-size: 0.72rem;
  color: ${(p) => p.theme.mutedForeground};
  background: ${(p) => p.theme.muted}18;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 8px;
  padding: 11px 13px;
  line-height: 1.5;
`;

/** Borderless helper copy (replaces boxed InfoNote where a card border feels redundant). */
const SectionHelper = styled.p`
  font-size: 0.75rem;
  line-height: 1.55;
  color: ${(p) => p.theme.mutedForeground};
  margin: 0 0 12px;
`;

const UtilitySetupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

/** Additional fixed-fee utilities: stack rows without an extra card border (toggle row keeps its own edge). */
const AdditionalUtilitiesStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const UtilityCard = styled.div`
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 10px;
  padding: 12px;
  background: ${(p) => p.theme.muted}15;
`;

const UtilityCardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const UtilityCardTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${(p) => p.theme.foreground};
`;

const IconBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid ${(p) => p.theme.border};
  background: transparent;
  color: ${(p) => p.theme.mutedForeground};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    color: ${(p) => p.theme.destructive};
    border-color: ${(p) => p.theme.destructive}66;
    background: ${(p) => p.theme.destructive}10;
  }
`;

const TinyText = styled.div`
  font-size: 0.7rem;
  color: ${(p) => p.theme.mutedForeground};
  margin-top: 4px;
`;

const MultiTypeGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const TypeChip = styled.button<{ $on: boolean }>`
  border-radius: 16px;
  border: none;
  background: ${(p) =>
    p.$on ? `${p.theme.accent}16` : p.theme.background};
  color: ${(p) =>
    p.$on ? p.theme.accentForeground : p.theme.mutedForeground};
  font-size: 0.72rem;
  font-weight: 600;
  padding: 4px 10px;
  cursor: pointer;
  transition: box-shadow 0.18s ease, background 0.18s ease;
  box-shadow: ${(p) =>
    p.$on
      ? `0 2px 10px -3px color-mix(in oklch, ${p.theme.foreground} 12%, transparent), 0 1px 8px -3px color-mix(in oklch, ${p.theme.accent} 32%, transparent)`
      : `0 1px 3px color-mix(in oklch, ${p.theme.foreground} 8%, transparent)`};

  &:hover {
    box-shadow: ${(p) =>
      `0 3px 12px -3px color-mix(in oklch, ${p.theme.foreground} 14%, transparent)`};
  }
`;

const UtilityHouseGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const UtilityHouseChip = styled.button<{ $on: boolean; $disabled?: boolean }>`
  border-radius: 14px;
  border: 2px solid
    ${(p) => (p.$on ? "rgb(16 185 129)" : "transparent")};
  background: ${(p) =>
    p.$on
      ? `color-mix(in srgb, rgb(16 185 129) 38%, ${p.theme.card})`
      : p.theme.background};
  color: ${(p) =>
    p.$on ? "rgb(6 95 70)" : p.theme.mutedForeground};
  font-size: 0.72rem;
  font-weight: 600;
  padding: 4px 10px;
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(p) => (p.$disabled ? 0.45 : 1)};
  transition: box-shadow 0.18s ease, background 0.18s ease, border-color 0.18s ease;
  box-shadow: ${(p) =>
    p.$on && !p.$disabled
      ? `0 2px 10px -3px color-mix(in oklch, ${p.theme.foreground} 12%, transparent), 0 1px 8px -3px rgba(16, 185, 129, 0.35)`
      : `0 1px 3px color-mix(in oklch, ${p.theme.foreground} 8%, transparent)`};

  &:hover {
    box-shadow: ${(p) =>
      p.$disabled
        ? `0 1px 3px color-mix(in oklch, ${p.theme.foreground} 8%, transparent)`
        : `0 3px 12px -3px color-mix(in oklch, ${p.theme.foreground} 14%, transparent)`};
    background: ${(p) =>
      p.$on && !p.$disabled
        ? `color-mix(in srgb, rgb(16 185 129) 48%, ${p.theme.card})`
        : undefined};
  }
`;

const ChannelAlert = styled.div<{ $blocking: boolean }>`
  background: ${(p) => `${p.theme.warning}18`};
  border: 1px solid ${(p) => `${p.theme.warning}55`};
  border-left: 4px solid ${(p) => p.theme.warning};
  border-radius: 8px;
  padding: 12px 14px;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  animation: ${(p) => (p.$blocking ? "none" : "none")};
`;

const ChannelAlertTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.8rem;
  font-weight: 700;
  color: ${(p) => p.theme.foreground};
`;

const ChannelAlertBody = styled.div`
  font-size: 0.73rem;
  color: ${(p) => p.theme.foreground};
  line-height: 1.55;
`;

const ChannelAlertBtn = styled.button`
  align-self: flex-start;
  padding: 5px 12px;
  border-radius: 6px;
  border: 1px solid ${(p) => p.theme.warning};
  background: transparent;
  color: ${(p) => p.theme.foreground};
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 2px;
  transition: background 0.15s;

  &:hover {
    background: ${(p) => `${p.theme.warning}22`};
  }
`;

// Select
const StyledSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 8px;
  font-size: 0.875rem;
  background: ${(p) => p.theme.background};
  color: ${(p) => p.theme.foreground};
  cursor: pointer;
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.ring};
    box-shadow: 0 0 0 2px ${(p) => p.theme.ring}20;
  }

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  min-height: 180px;
  padding: 10px 12px;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 8px;
  font-size: 0.84rem;
  background: ${(p) => p.theme.background};
  color: ${(p) => p.theme.foreground};
  resize: vertical;
  line-height: 1.5;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.ring};
    box-shadow: 0 0 0 2px ${(p) => p.theme.ring}20;
  }
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 11px 14px;
  border-radius: 8px;
  background: ${(p) => p.theme.destructive}10;
  border: 1px solid ${(p) => p.theme.destructive}30;
  color: ${(p) => p.theme.destructive};
  font-size: 0.8rem;
  margin-bottom: 16px;
`;

// ─── Footer ───────────────────────────────────────────────────────────────────
const Footer = styled.div`
  padding: 14px 24px;
  border-top: 1px solid ${(p) => p.theme.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-shrink: 0;
  background: ${(p) => p.theme.background};
`;

const FooterStep = styled.div`
  font-size: 0.75rem;
  color: ${(p) => p.theme.mutedForeground};

  strong {
    color: ${(p) => p.theme.foreground};
    font-weight: 600;
  }
`;

const FooterBtns = styled.div`
  display: flex;
  gap: 7px;
`;

const UNIT_TYPE_OPTIONS = [
  { value: "BEDSITTER", label: "Bedsitter" },
  { value: "STUDIO", label: "Studio" },
  { value: "ONE_BEDROOM", label: "1 Bedroom" },
  { value: "TWO_BEDROOM", label: "2 Bedroom" },
  { value: "THREE_BEDROOM", label: "3 Bedroom" },
  { value: "FOUR_BEDROOM", label: "4 Bedroom" },
  { value: "FIVE_BEDROOM", label: "5 Bedroom" },
  { value: "COMMERCIAL", label: "Commercial" },
];

const METERED_UTILITY_TYPE_OPTIONS = [
  { value: "ELECTRICITY", label: "Electricity" },
  { value: "WATER", label: "Water" },
  { value: "GAS", label: "Gas" },
];

const ADDITIONAL_UTILITY_OPTIONS: Array<{
  value: AdditionalUtilityChargeConfig["type"];
  label: string;
}> = [
  { value: "TRASH", label: "Trash" },
  { value: "INTERNET", label: "Internet" },
  { value: "SEWERAGE", label: "Sewerage" },
  { value: "SECURITY", label: "Security" },
  { value: "OTHER", label: "Other" },
];

const LEASE_DEFAULT_PARTIES = `PARTIES

I, {{landlord_name}}, of {{landlord_company}}, whose address is {{landlord_address}}, Phone {{landlord_phone}}, and Email {{landlord_email}}, hereby grant and enter into this Tenancy Agreement as the Landlord.

AND

I, {{tenant_name}}, holder of ID/Passport No. {{tenant_id}}, Phone {{tenant_phone}}, and Email {{tenant_email}}, hereby accept and agree to enter into this Tenancy Agreement as the Tenant, subject to the terms and conditions contained herein.

This Tenancy Agreement is made on {{agreement_date}}.`;

const LEASE_DEFAULT_KEY_TERMS = `KEY TERMS
• Monthly rent: —
• Deposit: —
• Lease period: —
• Partial payment allowed: —
• Payment installments: —
• Late fees: —`;

const LEASE_DEFAULT_GENERAL = `GENERAL CONDITIONS
This agreement represents the full understanding between both parties. Any amendments must be mutually agreed upon in writing.`;

const LEASE_DEFAULT_SIGNATURES = `DIGITAL SIGNATURES
Landlord: {{landlord_name}} – {{landlord_signature}} ({{landlord_signature_date}})
Tenant: {{tenant_name}} – {{tenant_signature}} ({{tenant_signature_date}})`;

const LEASE_DEFAULT_TEMPLATE = [
  LEASE_DEFAULT_PARTIES,
  LEASE_DEFAULT_KEY_TERMS,
  LEASE_DEFAULT_GENERAL,
  LEASE_DEFAULT_SIGNATURES,
].join("\n\n");

// ─── Component ────────────────────────────────────────────────────────────────
const AddPropertyDrawer: React.FC<AddPropertyDrawerProps> = ({
  isOpen,
  onClose,
  onSubmit,
  property,
  isLoading = false,
  onLocationPatched,
}) => {
  const toast = useToast();
  const { user } = useAuth();
  const storageFormKey = useMemo(
    () => `addPropertyDrawerForm:${user?.id ?? "anon"}`,
    [user?.id],
  );
  const storageStepKey = useMemo(
    () => `addPropertyDrawerStep:${user?.id ?? "anon"}`,
    [user?.id],
  );

  useEffect(() => {
    if (!user?.id) return;
    try {
      const userForm = `addPropertyDrawerForm:${user.id}`;
      const userStep = `addPropertyDrawerStep:${user.id}`;
      const anonForm = "addPropertyDrawerForm:anon";
      const anonStep = "addPropertyDrawerStep:anon";
      if (!localStorage.getItem(userForm)) {
        const a = localStorage.getItem(anonForm);
        if (a) {
          localStorage.setItem(userForm, a);
          localStorage.removeItem(anonForm);
        }
      }
      if (!localStorage.getItem(userStep)) {
        const s = localStorage.getItem(anonStep);
        if (s) {
          localStorage.setItem(userStep, s);
          localStorage.removeItem(anonStep);
        }
      }
    } catch {
      /* ignore */
    }
  }, [user?.id]);

  const [, setSearchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingLocationPatch, setIsSavingLocationPatch] = useState(false);
  const [channels, setChannels] = useState<PayHeroChannel[]>([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [propertyOwners, setPropertyOwners] = useState<PropertyOwner[]>([]);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const lastLocationPatchSignature = React.useRef<string>("");

  const defaultForm: PropertyFormData = {
    name: "",
    location: "",
    address: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    county: "",
    country: "",
    postalCode: "",
    latitude: null,
    longitude: null,
    placeId: "",
    geocodeProvider: "",
    geocodeConfidence: null,
    ownerName: "",
    ownerPhone: "",
    ownerEmail: "",
    ownerNotes: "",
    propertyOwnerId: "",
    type: "RESIDENTIAL",
    totalUnits: 1,
    unitNamingConvention: "SEQUENTIAL",
    customNamingPattern: "",
    unitTypeConfigs: [],
    allowPartialPayments: false,
    allow2Installments: true,
    allow3Installments: true,
    allow4Installments: true,
    enableLateFees: false,
    payheroChannelId: "",
    utilityBillingMode: "SHARED",
    sharedUtilityGroups: [],
    individualUtilityMappings: [],
    additionalUtilityCharges: ADDITIONAL_UTILITY_OPTIONS.map((opt) => ({
      type: opt.value,
      enabled: false,
      amount: "",
    })),
    defaultAgreementContent: LEASE_DEFAULT_TEMPLATE,
    unitNamingGroups: [{ id: "grp-1", prefix: "A", start: "1", end: "10" }],
  };

  const [form, setForm] = useState<PropertyFormData>(defaultForm);

  // Channel requirement: no default channel and no property-specific one selected
  const noDefaultChannel =
    !channelsLoading && !channels.some((c) => c.isDefault);
  const channelBlocking =
    !property && noDefaultChannel && !form.payheroChannelId;

  // Persist
  const persist = useCallback(
    (data: PropertyFormData, s: number) => {
      if (!property) {
        try {
          localStorage.setItem(storageFormKey, JSON.stringify(data));
          localStorage.setItem(storageStepKey, String(s));
        } catch {
          /* ignore */
        }
      }
    },
    [property, storageFormKey, storageStepKey],
  );

  // Init form on open
  useEffect(() => {
    if (!isOpen) return;

    if (property) {
      // Edit mode — populate from property
      let configs: UnitTypeConfig[] = [];
      if (property.unitTypeConfigs?.length) {
        const counts = new Map<string, number>();
        const houseMap = new Map<string, string[]>();
        property.units?.forEach((u: any) => {
          const t = u.unitType || "BEDSITTER";
          counts.set(t, (counts.get(t) || 0) + 1);
          houseMap.set(t, [
            ...(houseMap.get(t) || []),
            String(u.unitNumber || "").toUpperCase(),
          ]);
        });
        configs = property.unitTypeConfigs.map((c: any, i: number) => ({
          id: c.id || `${Date.now()}-${i}`,
          type: c.unitType || "",
          count: String(counts.get(c.unitType) || 0),
          houseNumbers: houseMap.get(c.unitType) || [],
          rent: String(c.rent || 0),
          deposit: String(c.deposit || 0),
          size: "",
          bedrooms: "",
          bathrooms: "",
        }));
      } else if (property.units?.length) {
        const map = new Map<
          string,
          {
            count: number;
            rent: number;
            deposit: number;
            size?: number;
            bedrooms?: number;
            bathrooms?: number;
          }
        >();
        const houseMap = new Map<string, string[]>();
        property.units.forEach((u: any) => {
          const t = u.unitType || "BEDSITTER";
          const ex = map.get(t) || {
            count: 0,
            rent: u.rent || 0,
            deposit: u.deposit || 0,
            size: u.size,
            bedrooms: u.bedrooms,
            bathrooms: u.bathrooms,
          };
          ex.count++;
          map.set(t, ex);
          houseMap.set(t, [
            ...(houseMap.get(t) || []),
            String(u.unitNumber || "").toUpperCase(),
          ]);
        });
        configs = Array.from(map.entries()).map(([t, d], i) => ({
          id: `${Date.now()}-${i}`,
          type: t,
          count: String(d.count),
          houseNumbers: houseMap.get(t) || [],
          rent: String(d.rent),
          deposit: String(d.deposit),
          size: d.size !== undefined ? String(d.size) : "",
          bedrooms: d.bedrooms !== undefined ? String(d.bedrooms) : "",
          bathrooms: d.bathrooms !== undefined ? String(d.bathrooms) : "",
        }));
      }
      let utilityBillingMode: "SHARED" | "INDIVIDUAL" = "SHARED";
      let sharedUtilityGroups: SharedUtilityGroupConfig[] = [];
      let individualUtilityMappings: IndividualUtilityMappingConfig[] = [];
      let additionalUtilityCharges = ADDITIONAL_UTILITY_OPTIONS.map((opt) => ({
        type: opt.value,
        enabled: false,
        amount: "",
      }));

      if (property.utilityConfigs?.length) {
        utilityBillingMode = property.utilityConfigs[0].mode as
          | "SHARED"
          | "INDIVIDUAL";
        if (utilityBillingMode === "SHARED") {
          sharedUtilityGroups = property.utilityConfigs.map((c: any) => ({
            id: c.id || Date.now().toString(),
            groupName: c.name,
            utilityType: c.utilityType,
            billingMethod: c.billingMethod || "LUMP_SUM_DISTRIBUTION",
            ratePerUnit: c.ratePerUnit ? String(c.ratePerUnit) : "",
            houseNumbers: c.houseNumbers ? c.houseNumbers.split(",") : [],
          }));
        } else {
          individualUtilityMappings = property.utilityConfigs.map((c: any) => ({
            id: c.id || Date.now().toString(),
            utilityType: c.utilityType,
            unitType: c.appliesToUnitTypes?.[0] || "",
            houseNumbers: c.houseNumbers ? c.houseNumbers.split(",") : [],
          }));
        }
      }
      if (property.additionalCharges?.length) {
        additionalUtilityCharges = additionalUtilityCharges.map((charge) => {
          const found = property.additionalCharges.find(
            (c: any) => c.type === charge.type && c.enabled,
          );
          if (found)
            return { ...charge, enabled: true, amount: String(found.amount) };
          return charge;
        });
      }

      setForm({
        name: property.name || "",
        location: property.location || "",
        address: property.address || "",
        addressLine1: property.addressLine1 || "",
        addressLine2: property.addressLine2 || "",
        city: property.city || "",
        county: property.county || "",
        country: property.country || "",
        postalCode: property.postalCode || "",
        latitude:
          typeof property.latitude === "number" ? property.latitude : null,
        longitude:
          typeof property.longitude === "number" ? property.longitude : null,
        placeId: property.placeId || "",
        geocodeProvider: property.geocodeProvider || "",
        geocodeConfidence:
          typeof property.geocodeConfidence === "number"
            ? property.geocodeConfidence
            : null,
        ownerName: property.ownerName || "",
        ownerPhone: property.ownerPhone || "",
        ownerEmail: property.ownerEmail || "",
        ownerNotes: property.ownerNotes || "",
        propertyOwnerId: property.propertyOwnerId || "",
        type: property.type || "RESIDENTIAL",
        totalUnits: property.totalUnits || property.units?.length || 1,
        unitNamingConvention: property.unitNamingConvention || "SEQUENTIAL",
        customNamingPattern: property.customNamingPattern || "",
        unitNamingGroups: parseUnitNamingGroupsFromProperty(
          (property as { unitNamingGroups?: unknown }).unitNamingGroups,
        ),
        unitTypeConfigs: configs,
        allowPartialPayments: !!property.allowPartialPayments,
        allow2Installments: property.allow2Installments !== false,
        allow3Installments: property.allow3Installments !== false,
        allow4Installments: property.allow4Installments !== false,
        enableLateFees: !!property.enableLateFees,
        payheroChannelId: property.payHeroChannelId || "",
        utilityBillingMode,
        sharedUtilityGroups,
        individualUtilityMappings,
        additionalUtilityCharges,
        defaultAgreementContent:
          property.defaultAgreementContent || LEASE_DEFAULT_TEMPLATE,
      });
      if (typeof property.latitude === "number" && typeof property.longitude === "number") {
        lastLocationPatchSignature.current = `${property.latitude.toFixed(6)}:${property.longitude.toFixed(6)}:${property.placeId || ""}`;
      } else {
        lastLocationPatchSignature.current = "";
      }
      setStep(1);
      try {
        localStorage.removeItem(storageFormKey);
        localStorage.removeItem(storageStepKey);
      } catch {
        /* ok */
      }
    } else {
      // New — restore from storage (migrate legacy unscoped keys once)
      try {
        let saved = localStorage.getItem(storageFormKey);
        let savedStep = localStorage.getItem(storageStepKey);
        if (!saved) {
          const legacy = localStorage.getItem("addPropertyDrawerForm");
          if (legacy) {
            saved = legacy;
            localStorage.removeItem("addPropertyDrawerForm");
          }
        }
        if (!savedStep) {
          const legacyStep = localStorage.getItem("addPropertyDrawerStep");
          if (legacyStep) {
            savedStep = legacyStep;
            localStorage.removeItem("addPropertyDrawerStep");
          }
        }
        if (saved) {
          const parsed = JSON.parse(saved) as Partial<PropertyFormData>;
          setForm({
            ...defaultForm,
            ...parsed,
            unitTypeConfigs: Array.isArray(parsed.unitTypeConfigs)
              ? parsed.unitTypeConfigs.map((cfg: any) => ({
                  ...cfg,
                  houseNumbers: parseHouseNumbers(cfg.houseNumbers),
                }))
              : [],
            utilityBillingMode:
              parsed.utilityBillingMode === "INDIVIDUAL"
                ? "INDIVIDUAL"
                : "SHARED",
            sharedUtilityGroups: Array.isArray(parsed.sharedUtilityGroups)
              ? parsed.sharedUtilityGroups.map((g: any) => ({
                  ...g,
                  houseNumbers: parseHouseNumbers(g.houseNumbers),
                }))
              : [],
            individualUtilityMappings: Array.isArray(
              parsed.individualUtilityMappings,
            )
              ? parsed.individualUtilityMappings.map((m: any) => ({
                  ...m,
                  houseNumbers: parseHouseNumbers(m.houseNumbers),
                }))
              : [],
            additionalUtilityCharges: Array.isArray(
              parsed.additionalUtilityCharges,
            )
              ? parsed.additionalUtilityCharges
              : defaultForm.additionalUtilityCharges,
            unitNamingGroups: Array.isArray(parsed.unitNamingGroups)
              ? parsed.unitNamingGroups
              : defaultForm.unitNamingGroups,
          });
          if (savedStep) setStep(Math.min(5, Math.max(1, parseInt(savedStep))));
        } else {
          setForm(defaultForm);
          setStep(1);
        }
      } catch {
        setForm(defaultForm);
        setStep(1);
      }
    }
    setErrors({});
  }, [property, isOpen, storageFormKey, storageStepKey]);

  // Persist on change
  useEffect(() => {
    if (isOpen) persist(form, step);
  }, [form, step, isOpen, persist]);

  // Load channels + property owners
  useEffect(() => {
    if (!isOpen || !user?.id) return;
    let cancelled = false;
    setChannelsLoading(true);
    setOwnersLoading(true);
    (async () => {
      try {
        const meRes = await apiService.get("/auth/me");
        const me = meRes.data?.user || meRes.data;
        const effectiveId = me?.createdById || me?.id || user.id;
        const r = await payheroChannelService.getChannels(effectiveId);
        if (!cancelled && r.success && r.data) setChannels(r.data);
      } catch {
        /* silent */
      } finally {
        if (!cancelled) setChannelsLoading(false);
      }
    })();
    (async () => {
      try {
        const owners = await organizationService.listPropertyOwners();
        if (!cancelled) setPropertyOwners(owners);
      } catch {
        if (!cancelled) setPropertyOwners([]);
      } finally {
        if (!cancelled) setOwnersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, user?.id]);

  // Escape key & body scroll lock
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handler);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  // Field updater
  const set = <K extends keyof PropertyFormData>(
    field: K,
    value: PropertyFormData[K],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const n = { ...prev };
      delete n[field as string];
      return n;
    });
  };

  const handleAddressSelection = async (selection: AddressSelection) => {
    setForm((prev) => ({
      ...prev,
      address: selection.address,
      location: selection.location || prev.location,
      addressLine1: selection.addressLine1 || "",
      city: selection.city || "",
      county: selection.county || "",
      country: selection.country || prev.country || "",
      postalCode: selection.postalCode || "",
      latitude: selection.latitude,
      longitude: selection.longitude,
      placeId: selection.placeId || "",
      geocodeProvider: selection.geocodeProvider,
      geocodeConfidence: selection.geocodeConfidence,
    }));

    if (!property?.id || selection.userInitiated === false) return;

    const signature = `${selection.latitude.toFixed(6)}:${selection.longitude.toFixed(6)}:${selection.placeId || ""}`;
    if (lastLocationPatchSignature.current === signature) return;
    lastLocationPatchSignature.current = signature;

    try {
      setIsSavingLocationPatch(true);
      const response = await apiService.updateProperty(property.id, {
        address: selection.address,
        location: selection.location || form.location,
        addressLine1: selection.addressLine1 || "",
        city: selection.city || "",
        county: selection.county || "",
        country: selection.country || form.country || "KE",
        postalCode: selection.postalCode || "",
        latitude: selection.latitude,
        longitude: selection.longitude,
        placeId: selection.placeId || "",
        geocodeProvider: selection.geocodeProvider,
        geocodeConfidence: selection.geocodeConfidence,
      });
      onLocationPatched?.(response.data?.property || response.data);
      toast.success("Location saved", {
        description:
          "Pinned location was saved immediately and synced across property cards and map actions.",
      });
    } catch (error: any) {
      toast.error("Could not auto-save location", {
        description: error?.response?.data?.error || "Save manually with Update property.",
      });
    } finally {
      setIsSavingLocationPatch(false);
    }
  };

  const addSharedUtilityGroup = () => {
    set("sharedUtilityGroups", [
      ...(form.sharedUtilityGroups || []),
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        utilityType: "ELECTRICITY",
        groupName: "",
        houseNumbers: [],
        billingMethod: "LUMP_SUM_DISTRIBUTION",
        ratePerUnit: "",
      },
    ]);
  };

  const updateSharedUtilityGroup = (
    id: string,
    patch: Partial<SharedUtilityGroupConfig>,
  ) => {
    set(
      "sharedUtilityGroups",
      (form.sharedUtilityGroups || []).map((item) =>
        item.id === id ? { ...item, ...patch } : item,
      ),
    );
  };

  const removeSharedUtilityGroup = (id: string) => {
    set(
      "sharedUtilityGroups",
      (form.sharedUtilityGroups || []).filter((item) => item.id !== id),
    );
  };

  const toggleSharedUtilityHouse = (groupId: string, houseNo: string) => {
    const group = (form.sharedUtilityGroups || []).find(
      (g) => g.id === groupId,
    );
    if (!group) return;
    const currentHouses = parseHouseNumbers(group.houseNumbers);
    const next = currentHouses.includes(houseNo)
      ? currentHouses.filter((h) => h !== houseNo)
      : [...currentHouses, houseNo];
    updateSharedUtilityGroup(groupId, { houseNumbers: next });
  };

  const addIndividualUtilityMapping = () => {
    set("individualUtilityMappings", [
      ...(form.individualUtilityMappings || []),
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        utilityType: "ELECTRICITY",
        unitType: "",
        houseNumbers: [],
        billingMethod: "LUMP_SUM_DISTRIBUTION",
        ratePerUnit: "",
      },
    ]);
  };

  const updateIndividualUtilityMapping = (
    id: string,
    patch: Partial<IndividualUtilityMappingConfig>,
  ) => {
    set(
      "individualUtilityMappings",
      (form.individualUtilityMappings || []).map((item) =>
        item.id === id ? { ...item, ...patch } : item,
      ),
    );
  };

  const removeIndividualUtilityMapping = (id: string) => {
    set(
      "individualUtilityMappings",
      (form.individualUtilityMappings || []).filter((item) => item.id !== id),
    );
  };

  const toggleIndividualUtilityHouse = (mappingId: string, houseNo: string) => {
    const mapping = (form.individualUtilityMappings || []).find(
      (m) => m.id === mappingId,
    );
    if (!mapping) return;
    const currentHouses = parseHouseNumbers(mapping.houseNumbers);
    const next = currentHouses.includes(houseNo)
      ? currentHouses.filter((h) => h !== houseNo)
      : [...currentHouses, houseNo];
    updateIndividualUtilityMapping(mappingId, { houseNumbers: next });
  };

  const updateAdditionalUtilityCharge = (
    type: AdditionalUtilityChargeConfig["type"],
    patch: Partial<AdditionalUtilityChargeConfig>,
  ) => {
    set(
      "additionalUtilityCharges",
      (form.additionalUtilityCharges || []).map((item) =>
        item.type === type ? { ...item, ...patch } : item,
      ),
    );
  };

  const getUnitTypeLabel = (value: string) =>
    UNIT_TYPE_OPTIONS.find((opt) => opt.value === value)?.label || value;

  const parseHouseNumbers = (value?: string | string[]): string[] => {
    if (Array.isArray(value)) {
      return value.map((v) => String(v).trim().toUpperCase()).filter(Boolean);
    }
    return (value || "")
      .split(/[\n,]/g)
      .map((v) => v.trim().toUpperCase())
      .filter(Boolean);
  };

  const configuredUnitTypes = Array.from(
    new Set((form.unitTypeConfigs || []).map((u) => u.type).filter(Boolean)),
  );

  const normalizeGroupPrefix = (prefix?: string): string =>
    String(prefix || "").trim().toUpperCase();

  const generateGroupedUnitNames = useCallback((): string[] => {
    const groups = form.unitNamingGroups || [];
    const names: string[] = [];
    const seen = new Set<string>();
    for (const g of groups) {
      const start = Number.parseInt(String(g.start || "").trim(), 10);
      const end = Number.parseInt(String(g.end || "").trim(), 10);
      if (!Number.isFinite(start) || !Number.isFinite(end) || start <= 0 || end < start) {
        continue;
      }
      const prefix = normalizeGroupPrefix(g.prefix);
      for (let i = start; i <= end; i++) {
        const n = `${prefix}${i}`.toUpperCase();
        if (!seen.has(n)) {
          names.push(n);
          seen.add(n);
        }
      }
    }
    return names;
  }, [form.unitNamingGroups]);

  const generateUnitNames = (count: number): string[] => {
    if (form.unitNamingConvention === "GROUPED_RANGES") {
      const grouped = generateGroupedUnitNames();
      return grouped.slice(0, count);
    }
    const names: string[] = [];
    const seen = new Set<string>();
    for (let i = 0; i < count; i++) {
      let n = "";
      switch (form.unitNamingConvention) {
        case "SEQUENTIAL":
          n = String(i + 1);
          break;
        case "FLOOR_UNIT":
          n = `${Math.floor(i / 10) + 1}${String((i % 10) + 1).padStart(2, "0")}`;
          break;
        case "BLOCK_UNIT":
          n = `${String.fromCharCode(65 + Math.floor(i / 10))}${(i % 10) + 1}`;
          break;
        case "CUSTOM":
          n = form.customNamingPattern
            ? (() => {
                const floor = Math.floor(i / 10) + 1;
                const unit = (i % 10) + 1;
                const block = String.fromCharCode(65 + Math.floor(i / 10));
                return form.customNamingPattern
                  .replace(/\{\s*block\s*(?::\s*(\d+)\s*)?\}/gi, (_m, pad) =>
                    pad ? block.padStart(Number(pad), "A") : block,
                  )
                  .replace(/\{\s*floor\s*(?::\s*(\d+)\s*)?\}/gi, (_m, pad) =>
                    pad
                      ? String(floor).padStart(Number(pad), "0")
                      : String(floor),
                  )
                  .replace(
                    /\{\s*(unit|apt)\s*(?::\s*(\d+)\s*)?\}/gi,
                    (_m, _key, pad) =>
                      pad
                        ? String(unit).padStart(Number(pad), "0")
                        : String(unit),
                  )
                  .replace(/\bBLOCK\b/gi, block)
                  .replace(/\bFLOOR\b/gi, String(floor))
                  .replace(/\bUNIT\b/gi, String(unit))
                  .replace(/\bAPT\b/gi, String(unit));
              })()
            : `Unit ${i + 1}`;
          break;
        default:
          n = `Unit ${i + 1}`;
      }
      const candidate = n.toUpperCase();
      if (!seen.has(candidate)) {
        names.push(candidate);
        seen.add(candidate);
      } else {
        const fallback = `${candidate}-${i + 1}`;
        names.push(fallback);
        seen.add(fallback);
      }
    }
    return names;
  };

  const availableUnitNumbers: string[] = (() => {
    const generated = generateUnitNames(Math.max(0, form.totalUnits || 0));
    if (generated.length) {
      return generated;
    }
    const existingUnitNumbers: string[] = (property?.units || [])
      .map((u: any) => String(u?.unitNumber || "").trim().toUpperCase())
      .filter(Boolean);
    return Array.from(new Set<string>(existingUnitNumbers));
  })();

  // Validate a step
  const validate = (s: number): boolean => {
    const errs: FormErrors = {};

    if (s === 1) {
      if (!form.name.trim()) errs.name = "Property name is required";
      else if (form.name.length < 3)
        errs.name = "At least 3 characters required";
      if (!form.location.trim()) errs.location = "Location is required";
      else if (form.location.length < 5)
        errs.location = "At least 5 characters required";
      if (!form.address.trim()) errs.address = "Address is required";
      else if (form.address.length < 5)
        errs.address = "At least 5 characters required";
      if (form.totalUnits < 1) errs.totalUnits = "At least 1 unit required";
      if (form.totalUnits > 1000) errs.totalUnits = "Maximum 1000 units";
      // Block new properties if no payment channel is available
      if (!property && !channelsLoading) {
        const hasDefault = channels.some((c) => c.isDefault);
        if (!hasDefault && !form.payheroChannelId) {
          errs.channel =
            channels.length === 0
              ? "Set up a payment channel in Settings before adding properties"
              : "Select a channel for this property or set a default in Settings";
        }
      }
    }
    if (s === 2) {
      if (form.unitNamingConvention === "GROUPED_RANGES") {
        const groups = form.unitNamingGroups || [];
        if (!groups.length) {
          errs.unitNamingGroups = "Add at least one naming group";
        } else {
          const seen = new Set<string>();
          let produced = 0;
          for (const g of groups) {
            const start = Number.parseInt(String(g.start || "").trim(), 10);
            const end = Number.parseInt(String(g.end || "").trim(), 10);
            if (!Number.isFinite(start) || !Number.isFinite(end) || start <= 0 || end < start) {
              errs.unitNamingGroups = "Each group needs valid start/end (end >= start)";
              break;
            }
            const prefix = normalizeGroupPrefix(g.prefix);
            for (let i = start; i <= end; i++) {
              const n = `${prefix}${i}`.toUpperCase();
              if (seen.has(n)) {
                errs.unitNamingGroups = `Duplicate unit name in groups: ${n}`;
                break;
              }
              seen.add(n);
              produced += 1;
            }
            if (errs.unitNamingGroups) break;
          }
          if (!errs.unitNamingGroups && produced !== form.totalUnits) {
            errs.unitNamingGroups = `Grouped names (${produced}) must equal total units (${form.totalUnits})`;
          }
        }
      }
      if (
        form.unitNamingConvention === "CUSTOM" &&
        !form.customNamingPattern?.trim()
      ) {
        errs.customNamingPattern = "Custom pattern is required";
      }
    }
    if (s === 3) {
      if (form.allowPartialPayments) {
        if (
          !form.allow2Installments &&
          !form.allow3Installments &&
          !form.allow4Installments
        ) {
          errs.installments = "Enable at least one installment option";
        }
      }
      if (!form.unitTypeConfigs || form.unitTypeConfigs.length === 0) {
        errs.unitTypeConfigs = "Add at least one unit type and assign houses";
      } else {
        const total = form.unitTypeConfigs.reduce(
          (acc, u) => acc + u.houseNumbers.length,
          0,
        );
        if (total !== form.totalUnits)
          errs.unitTypeConfigs = `Unit type counts (${total}) must equal total units (${form.totalUnits})`;

        const assigned = new Set<string>();
        for (const u of form.unitTypeConfigs) {
          if (!u.type) {
            errs.unitTypeConfigs = "All unit types must be selected";
            break;
          }
          const selectedHouses = parseHouseNumbers(u.houseNumbers);
          if (!selectedHouses.length) {
            errs.unitTypeConfigs =
              "Select at least one house for each unit type";
            break;
          }
          for (const h of selectedHouses) {
            if (assigned.has(h)) {
              errs.unitTypeConfigs = `House ${h} is assigned to multiple unit types`;
              break;
            }
            assigned.add(h);
          }
          if (errs.unitTypeConfigs) break;
          if (!u.rent || parseFloat(u.rent) < 0) {
            errs.unitTypeConfigs = "Enter valid rent for all types";
            break;
          }
          if (u.size && parseFloat(u.size) <= 0) {
            errs.unitTypeConfigs = "Unit size must be greater than 0";
            break;
          }
          if (u.bedrooms && parseInt(u.bedrooms) < 0) {
            errs.unitTypeConfigs = "Bedrooms cannot be negative";
            break;
          }
          if (u.bathrooms && parseInt(u.bathrooms) < 0) {
            errs.unitTypeConfigs = "Bathrooms cannot be negative";
            break;
          }
        }
      }
    }

    if (s === 4) {
      if (form.utilityBillingMode === "SHARED") {
        const groups = form.sharedUtilityGroups || [];
        const seenPerType = new Map<string, Set<string>>();

        for (const g of groups) {
          if (!g.groupName.trim()) {
            errs.utilitySetup = "Each shared utility group needs a group name";
            break;
          }
          if (!g.utilityType) {
            errs.utilitySetup =
              "Each shared utility group needs a utility type";
            break;
          }
          const houses = parseHouseNumbers(g.houseNumbers);
          if (!houses.length) {
            errs.utilitySetup =
              "Add house numbers for each shared utility group";
            break;
          }

          const seen = seenPerType.get(g.utilityType) || new Set<string>();
          for (const h of houses) {
            if (seen.has(h)) {
              errs.utilitySetup = `House number ${h} is duplicated in ${g.utilityType}. A unit can only be in one group per utility type.`;
              break;
            }
            seen.add(h);
          }
          if (errs.utilitySetup) break;
          seenPerType.set(g.utilityType, seen);
        }
      } else {
        const mappings = form.individualUtilityMappings || [];
        for (const m of mappings) {
          if (!m.utilityType) {
            errs.utilitySetup = "Each utility mapping needs a utility type";
            break;
          }
          if (!m.unitType) {
            errs.utilitySetup = "Select a unit type for each utility mapping";
            break;
          }
          if (!parseHouseNumbers(m.houseNumbers).length) {
            errs.utilitySetup = `Add house numbers for ${getUnitTypeLabel(m.unitType)} mapping`;
            break;
          }
        }
      }

      for (const charge of form.additionalUtilityCharges || []) {
        if (charge.enabled && (!charge.amount || Number(charge.amount) <= 0)) {
          errs.utilitySetup = `Enter amount for ${charge.type.toLowerCase()} utility`;
          break;
        }
      }
    }

    if (s === 5) {
      if (!form.defaultAgreementContent?.trim()) {
        errs.defaultAgreementContent =
          "Lease agreement content is required";
      } else if (form.defaultAgreementContent.trim().length < 120) {
        errs.defaultAgreementContent =
          "Lease agreement content is too short";
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (validate(step)) setStep((s) => Math.min(5, s + 1));
  };

  const handleBack = () => setStep((s) => Math.max(1, s - 1));

  const handleSubmit = async () => {
    const v1 = validate(1);
    const v2 = validate(2);
    const v3 = validate(3);
    const v4 = validate(4);
    const v5 = validate(5);
    if (!v1 || !v2 || !v3 || !v4 || !v5) {
      if (!v1) setStep(1);
      else if (!v2) setStep(2);
      else if (!v3) setStep(3);
      else if (!v4) setStep(4);
      else setStep(5);
      return;
    }

    try {
      setIsSubmitting(true);
      const isGrouped =
        (form.unitNamingConvention || "SEQUENTIAL") === "GROUPED_RANGES";
      const payload: any = {
        name: form.name.trim(),
        location: form.location.trim(),
        ...(form.ownerName?.trim() ? { ownerName: form.ownerName.trim() } : { ownerName: null }),
        ...(form.ownerPhone?.trim() ? { ownerPhone: form.ownerPhone.trim() } : { ownerPhone: null }),
        ...(form.ownerEmail?.trim() ? { ownerEmail: form.ownerEmail.trim() } : { ownerEmail: null }),
        ...(form.ownerNotes?.trim() ? { ownerNotes: form.ownerNotes.trim() } : { ownerNotes: null }),
        ...(form.propertyOwnerId ? { propertyOwnerId: form.propertyOwnerId } : {}),
        type: form.type || "RESIDENTIAL",
        totalUnits: form.totalUnits,
        unitNamingConvention: form.unitNamingConvention || "SEQUENTIAL",
        customNamingPattern: isGrouped
          ? undefined
          : form.customNamingPattern || undefined,
        ...(isGrouped
          ? {
              unitNamingGroups: (form.unitNamingGroups || []).map((g) => ({
                prefix: String(g.prefix || "").trim(),
                start: Number.parseInt(String(g.start || "").trim(), 10),
                end: Number.parseInt(String(g.end || "").trim(), 10),
              })),
            }
          : property
            ? { unitNamingGroups: null }
            : {}),
        allowPartialPayments: !!form.allowPartialPayments,
        allow2Installments: form.allow2Installments !== false,
        allow3Installments: form.allow3Installments !== false,
        allow4Installments: form.allow4Installments !== false,
        enableLateFees: !!form.enableLateFees,
        payHeroChannelId:
          form.payheroChannelId === "" ? null : form.payheroChannelId,
        unitTypeConfigs: form.unitTypeConfigs?.length
          ? form.unitTypeConfigs.map((u) => ({
              unitType: u.type,
              count: u.houseNumbers.length,
              houseNumbers: u.houseNumbers.map((h) => h.toUpperCase()),
              rent: parseFloat(u.rent),
              deposit: parseFloat(u.deposit || "0"),
              ...(u.size ? { size: parseFloat(u.size) } : {}),
              ...(u.bedrooms ? { bedrooms: parseInt(u.bedrooms) } : {}),
              ...(u.bathrooms ? { bathrooms: parseInt(u.bathrooms) } : {}),
            }))
          : undefined,
        utilitySetup: [
          ...(form.utilityBillingMode === "SHARED"
            ? form.sharedUtilityGroups || []
            : []
          ).map((group) => ({
            name: group.groupName.trim(),
            utilityType: group.utilityType,
            mode: "SHARED" as const,
            appliesToUnitTypes: [],
            houseNumbers: (group.houseNumbers || []).join(","),
            enabled: true,
            billingMethod: group.billingMethod || "LUMP_SUM_DISTRIBUTION",
            ratePerUnit: group.ratePerUnit
              ? Number(group.ratePerUnit)
              : undefined,
          })),
          ...(form.utilityBillingMode === "INDIVIDUAL"
            ? form.individualUtilityMappings || []
            : []
          ).map((map) => ({
            name: `${map.utilityType} • ${getUnitTypeLabel(map.unitType)}`,
            utilityType: map.utilityType,
            mode: "INDIVIDUAL" as const,
            appliesToUnitTypes: map.unitType ? [map.unitType] : [],
            houseNumbers: (map.houseNumbers || []).join(","),
            enabled: true,
            billingMethod: map.billingMethod || "LUMP_SUM_DISTRIBUTION",
            ratePerUnit: map.ratePerUnit ? Number(map.ratePerUnit) : undefined,
          })),
        ],
        additionalUtilityCharges: (form.additionalUtilityCharges || [])
          .filter((c) => c.enabled && Number(c.amount) > 0)
          .map((c) => ({
            type: c.type,
            amount: Number(c.amount),
          })),
        defaultAgreementContent: form.defaultAgreementContent?.trim()
          ? form.defaultAgreementContent.trim()
          : null,
      };
      if (form.address?.trim()) payload.address = form.address.trim();
      if (form.addressLine1?.trim())
        payload.addressLine1 = form.addressLine1.trim();
      if (form.addressLine2?.trim())
        payload.addressLine2 = form.addressLine2.trim();
      if (form.city?.trim()) payload.city = form.city.trim();
      if (form.county?.trim()) payload.county = form.county.trim();
      if (form.country?.trim()) payload.country = form.country.trim().toUpperCase();
      if (form.postalCode?.trim()) payload.postalCode = form.postalCode.trim();
      if (typeof form.latitude === "number") payload.latitude = form.latitude;
      if (typeof form.longitude === "number") payload.longitude = form.longitude;
      if (form.placeId?.trim()) payload.placeId = form.placeId.trim();
      if (form.geocodeProvider?.trim())
        payload.geocodeProvider = form.geocodeProvider.trim();
      if (typeof form.geocodeConfidence === "number")
        payload.geocodeConfidence = form.geocodeConfidence;

      if (property) {
        // Parent (e.g. Properties.handleUpdateProperty) performs PUT — same as create flow.
        // Do not pass raw `form` (UI-only fields / string group rows) or the second request will 400.
        onSubmit({ ...payload, id: property.id } as any);
      } else {
        try {
          localStorage.removeItem(storageFormKey);
          localStorage.removeItem(storageStepKey);
        } catch {
          /* ok */
        }
        onSubmit(payload);
      }
    } catch (err: any) {
      const msg =
        err.response?.data?.error || err.message || "Something went wrong";
      setErrors((prev) => ({ ...prev, submit: msg }));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Preview unit names
  const previewNames = () => {
    // Always preview from the active naming strategy (including grouped ranges),
    // not from persisted existing unit numbers in edit mode.
    return generateUnitNames(Math.max(0, form.totalUnits || 0)).slice(0, 6);
  };

  const addNamingGroup = () => {
    const nextIndex = (form.unitNamingGroups?.length || 0) + 1;
    const nextPrefix = String.fromCharCode(64 + Math.min(nextIndex, 26));
    set("unitNamingGroups", [
      ...(form.unitNamingGroups || []),
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        prefix: nextPrefix,
        start: "1",
        end: "1",
      },
    ]);
  };

  const updateNamingGroup = (id: string, patch: Partial<UnitNamingGroup>) => {
    set(
      "unitNamingGroups",
      (form.unitNamingGroups || []).map((g) => (g.id === id ? { ...g, ...patch } : g)),
    );
  };

  const removeNamingGroup = (id: string) => {
    set(
      "unitNamingGroups",
      (form.unitNamingGroups || []).filter((g) => g.id !== id),
    );
  };

  const stepState = (s: number): "done" | "active" | "idle" =>
    s < step ? "done" : s === step ? "active" : "idle";

  if (!isOpen) return null;

  const busy = isLoading || isSubmitting;

  return (
    <Overlay onClick={onClose}>
      <Drawer onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <Header>
          <HeaderLeft>
            <IconBadge>
              <Building2 size={20} />
            </IconBadge>
            <TitleGroup>
              <DrawerTitle>
                {property ? "Edit Property" : "Add New Property"}
              </DrawerTitle>
              <DrawerSub>
                {property
                  ? "Update property details below"
                  : `Step ${step} of 5 · Fill in the details below`}
              </DrawerSub>
            </TitleGroup>
          </HeaderLeft>
          <CloseBtn onClick={onClose} title="Close (Esc)">
            <X size={17} />
          </CloseBtn>
        </Header>

        {/* Step progress */}
        <StepsRow>
          {(
            [
              { n: 1, label: "Basic Info" },
              { n: 2, label: "Naming" },
              { n: 3, label: "Units & Pricing" },
              { n: 4, label: "Utilities" },
              { n: 5, label: "Lease" },
            ] as const
          ).map(({ n, label }, idx, arr) => (
            <React.Fragment key={n}>
              <StepItem
                $clickable={n < step}
                onClick={() => {
                  if (n < step) setStep(n);
                }}
              >
                <StepDot $state={stepState(n)}>
                  {n < step ? <Check size={12} /> : n}
                </StepDot>
                <StepName $state={stepState(n)}>{label}</StepName>
              </StepItem>
              {idx < arr.length - 1 && <StepLine $done={n < step} />}
            </React.Fragment>
          ))}
        </StepsRow>

        {/* Scrollable body */}
        <Body>
          {errors.submit && (
            <ErrorBanner>
              <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
              {errors.submit}
            </ErrorBanner>
          )}

          {/* ── STEP 1 · Basic Info ── */}
          {step === 1 && (
            <>
              <SectionLabel>
                <Building2 size={12} />
                Property Details
              </SectionLabel>

              <TwoCol>
                <Field>
                  <FieldLbl htmlFor="drw-name">
                    <MapPin size={11} /> Name <span className="req">*</span>
                  </FieldLbl>
                  <Input
                    id="drw-name"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    placeholder="e.g. Sunrise Apartments"
                    error={!!errors.name}
                  />
                  {errors.name && (
                    <ErrMsg>
                      <AlertCircle size={10} />
                      {errors.name}
                    </ErrMsg>
                  )}
                </Field>

                <Field>
                  <FieldLbl htmlFor="drw-loc">
                    <MapPin size={11} /> Location <span className="req">*</span>
                  </FieldLbl>
                  <Input
                    id="drw-loc"
                    value={form.location}
                    onChange={(e) => set("location", e.target.value)}
                    placeholder="e.g. Kilimani, Nairobi"
                    error={!!errors.location}
                  />
                  {errors.location && (
                    <ErrMsg>
                      <AlertCircle size={10} />
                      {errors.location}
                    </ErrMsg>
                  )}
                </Field>
              </TwoCol>

              <FullCol>
                <Field>
                  <FieldLbl htmlFor="drw-addr">
                    <MapPin size={11} /> Approximate Address{" "}
                    <span className="req">*</span>
                  </FieldLbl>
                  <SectionHelper>
                    Start with an approximate address, then drag the map pin below to set the exact saved property location.
                  </SectionHelper>
                  <AddressLocationPicker
                    propertyId={property?.id}
                    value={form.address}
                    latitude={form.latitude ?? property?.latitude ?? null}
                    longitude={form.longitude ?? property?.longitude ?? null}
                    countryCode={form.country || "KE"}
                    skipAutoLocate={Boolean(
                      property?.id &&
                        (typeof (form.latitude ?? property?.latitude) === "number" ||
                          typeof (form.longitude ?? property?.longitude) === "number"),
                    )}
                    onAddressChange={(nextAddress) => set("address", nextAddress)}
                    onLocationSelect={handleAddressSelection}
                  />
                  {property?.id && isSavingLocationPatch && (
                    <SectionHelper>Saving pinned location...</SectionHelper>
                  )}
                  {(form.city || form.county || form.country) && (
                    <SectionHelper>
                      {[
                        form.city,
                        form.county,
                        form.country ? String(form.country).toUpperCase() : "",
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </SectionHelper>
                  )}
                  {errors.address && (
                    <ErrMsg>
                      <AlertCircle size={10} />
                      {errors.address}
                    </ErrMsg>
                  )}
                </Field>
              </FullCol>

              <FullCol>
                <SectionHelper>
                  Legal owner for this property. Firms: pick a client owner (or add one in Settings → Account → Organization). Soft fields below still create/find an owner if no selection.
                </SectionHelper>
              </FullCol>

              <FullCol>
                <Field>
                  <FieldLbl htmlFor="drw-property-owner">Property owner</FieldLbl>
                  <StyledSelect
                    id="drw-property-owner"
                    value={form.propertyOwnerId || ""}
                    onChange={(e) => {
                      const id = e.target.value;
                      const selected = propertyOwners.find((o) => o.id === id);
                      set("propertyOwnerId", id);
                      if (selected) {
                        setForm((prev) => ({
                          ...prev,
                          propertyOwnerId: id,
                          ownerName: selected.name || prev.ownerName,
                          ownerEmail: selected.email || prev.ownerEmail || "",
                          ownerPhone: selected.phone || prev.ownerPhone || "",
                          ownerNotes: selected.notes || prev.ownerNotes || "",
                        }));
                      }
                    }}
                    disabled={ownersLoading}
                  >
                    <option value="">
                      {ownersLoading
                        ? "Loading owners…"
                        : "Default / create from name below"}
                    </option>
                    {propertyOwners.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                        {o.isDefault ? " (default)" : ""}
                        {o.type === "COMPANY" ? " · company" : ""}
                      </option>
                    ))}
                  </StyledSelect>
                </Field>
              </FullCol>

              <TwoCol>
                <Field>
                  <FieldLbl htmlFor="drw-owner-name">Owner Name</FieldLbl>
                  <Input
                    id="drw-owner-name"
                    value={form.ownerName || ""}
                    onChange={(e) => {
                      set("ownerName", e.target.value);
                      // Free-text path — clear explicit owner link so backend findOrCreate runs
                      if (form.propertyOwnerId) set("propertyOwnerId", "");
                    }}
                    placeholder="e.g. Jane Kamau"
                  />
                </Field>
                <Field>
                  <FieldLbl htmlFor="drw-owner-phone">Owner Phone</FieldLbl>
                  <Input
                    id="drw-owner-phone"
                    value={form.ownerPhone || ""}
                    onChange={(e) => set("ownerPhone", e.target.value)}
                    placeholder="e.g. +254712345678"
                  />
                </Field>
              </TwoCol>

              <TwoCol>
                <Field>
                  <FieldLbl htmlFor="drw-owner-email">Owner Email</FieldLbl>
                  <Input
                    id="drw-owner-email"
                    value={form.ownerEmail || ""}
                    onChange={(e) => set("ownerEmail", e.target.value)}
                    placeholder="e.g. owner@example.com"
                  />
                </Field>
                <Field>
                  <FieldLbl htmlFor="drw-owner-notes">Owner Notes</FieldLbl>
                  <Input
                    id="drw-owner-notes"
                    value={form.ownerNotes || ""}
                    onChange={(e) => set("ownerNotes", e.target.value)}
                    placeholder="e.g. ID No., payout account hint, contact preference"
                  />
                </Field>
              </TwoCol>

              <FullCol>
                <Field>
                  <FieldLbl>
                    <Home size={11} /> Property Type
                  </FieldLbl>
                  <TypeGrid>
                    {(
                      [
                        {
                          value: "RESIDENTIAL",
                          label: "Residential",
                          Icon: Home,
                        },
                        {
                          value: "COMMERCIAL",
                          label: "Commercial",
                          Icon: Building2,
                        },
                        {
                          value: "MIXED_USE",
                          label: "Mixed Use",
                          Icon: Layers,
                        },
                      ] as const
                    ).map((opt) => {
                      const TypeIcon = opt.Icon;
                      return (
                        <TypeCard
                          key={opt.value}
                          type="button"
                          $active={form.type === opt.value}
                          onClick={() => set("type", opt.value)}
                        >
                          <span className="type-icon">
                            <TypeIcon size={22} strokeWidth={2} />
                          </span>
                          <span className="type-label">{opt.label}</span>
                        </TypeCard>
                      );
                    })}
                  </TypeGrid>
                </Field>
              </FullCol>

              <TwoCol>
                <Field>
                  <FieldLbl htmlFor="drw-units">
                    <Hash size={11} /> Total Units{" "}
                    <span className="req">*</span>
                  </FieldLbl>
                  <Input
                    id="drw-units"
                    type="number"
                    min="1"
                    max="1000"
                    value={form.totalUnits.toString()}
                    onChange={(e) =>
                      set("totalUnits", parseInt(e.target.value) || 1)
                    }
                    error={!!errors.totalUnits}
                  />
                  {errors.totalUnits && (
                    <ErrMsg>
                      <AlertCircle size={10} />
                      {errors.totalUnits}
                    </ErrMsg>
                  )}
                </Field>

                <Field>
                  <FieldLbl htmlFor="drw-channel">
                    <CreditCard size={11} /> Payment Channel
                    {noDefaultChannel && !form.payheroChannelId ? (
                      <span className="req">*</span>
                    ) : (
                      <span className="opt">(optional)</span>
                    )}
                  </FieldLbl>
                  <StyledSelect
                    id="drw-channel"
                    value={form.payheroChannelId || ""}
                    onChange={(e) => set("payheroChannelId", e.target.value)}
                    disabled={channelsLoading}
                    style={
                      noDefaultChannel && !form.payheroChannelId
                        ? { borderColor: "hsl(var(--warning))" }
                        : undefined
                    }
                  >
                    <option value="">
                      {channelsLoading
                        ? "Loading…"
                        : channels.length === 0
                          ? "— No channels —"
                          : "— Use default —"}
                    </option>
                    {channels.map((ch) => (
                      <option key={ch.id} value={ch.id}>
                        {ch.description}
                        {ch.isDefault ? " ✓" : ""}
                        {ch.shortCode ? ` · ${ch.shortCode}` : ""}
                      </option>
                    ))}
                  </StyledSelect>
                </Field>
              </TwoCol>

              {/* Channel warning banner */}
              {!channelsLoading && noDefaultChannel && (
                <ChannelAlert $blocking={channelBlocking}>
                  <ChannelAlertTitle>
                    <AlertCircle size={14} />
                    {channels.length === 0
                      ? "No payment channels configured"
                      : "No default payment channel"}
                  </ChannelAlertTitle>
                  <ChannelAlertBody>
                    {channels.length === 0
                      ? "Tenants cannot make payments without a payment channel. Please set one up in Settings → Payment Channels before adding a property."
                      : form.payheroChannelId
                        ? "This property will use the selected channel above. To avoid setting one per property, also set a default in Settings → Payment Channels."
                        : "No default channel is set. Select a specific channel for this property above, or go to Settings → Payment Channels to set a default."}
                  </ChannelAlertBody>
                  <ChannelAlertBtn
                    type="button"
                    onClick={() => {
                      markSettingsReturnToAddProperty();
                      onClose();
                      setSearchParams({ openSettings: "payment-channels" });
                    }}
                  >
                    Go to Settings →
                  </ChannelAlertBtn>
                </ChannelAlert>
              )}
            </>
          )}

          {/* ── STEP 3 · Configuration ── */}
          {step === 3 && (
            <>
              <SectionLabel>
                <Layers size={12} />
                Unit Types &amp; Rent
              </SectionLabel>

              <div
                style={{
                  marginBottom: 8,
                  color: "var(--muted-foreground)",
                  fontSize: 12,
                }}
              >
                Required: choose a unit type, then assign one or more houses to
                it.
              </div>

              <div style={{ marginBottom: 16 }}>
                <UnitTypesSection
                  unitTypes={form.unitTypeConfigs || []}
                  totalUnits={form.totalUnits}
                  availableUnitNumbers={availableUnitNumbers}
                  onAdd={() =>
                    set("unitTypeConfigs", [
                      ...(form.unitTypeConfigs || []),
                      {
                        id: Date.now().toString(),
                        type: "",
                        count: "",
                        houseNumbers: [],
                        rent: "",
                        deposit: "0",
                        size: "",
                        bedrooms: "",
                        bathrooms: "",
                      },
                    ])
                  }
                  onRemove={(id) =>
                    set(
                      "unitTypeConfigs",
                      form.unitTypeConfigs?.filter((u) => u.id !== id) || [],
                    )
                  }
                  onUpdate={(id, updates) =>
                    set(
                      "unitTypeConfigs",
                      form.unitTypeConfigs?.map((u) =>
                        u.id === id ? { ...u, ...updates } : u,
                      ) || [],
                    )
                  }
                  error={errors.unitTypeConfigs}
                />
              </div>

              <Divider />

              <SectionLabel>
                <DollarSign size={12} />
                Payment Configuration
              </SectionLabel>

              <ToggleRow
                onClick={() =>
                  set("allowPartialPayments", !form.allowPartialPayments)
                }
              >
                <ToggleInfo>
                  <div className="tg-title">Allow Partial Payments</div>
                  <div className="tg-desc">
                    Let tenants pay rent in installments
                  </div>
                </ToggleInfo>
                <ToggleSwitch $on={!!form.allowPartialPayments} />
              </ToggleRow>

              {form.allowPartialPayments && (
                <>
                  <div
                    style={{
                      fontSize: "0.72rem",
                      color: "var(--muted-foreground)",
                      marginBottom: 7,
                      paddingLeft: 2,
                    }}
                  >
                    Select allowed installment frequencies:
                  </div>
                  <InstallmentPills>
                    {[
                      {
                        key: "allow2Installments" as const,
                        label: "2 installments",
                      },
                      {
                        key: "allow3Installments" as const,
                        label: "3 installments",
                      },
                      {
                        key: "allow4Installments" as const,
                        label: "4 installments",
                      },
                    ].map(({ key, label }) => (
                      <Pill
                        key={key}
                        type="button"
                        $on={!!form[key]}
                        onClick={() => set(key, !form[key])}
                      >
                        {label}
                      </Pill>
                    ))}
                  </InstallmentPills>
                  {errors.installments && (
                    <ErrMsg>
                      <AlertCircle size={10} />
                      {errors.installments}
                    </ErrMsg>
                  )}
                </>
              )}

              <Divider />

              <ToggleRow
                onClick={() => set("enableLateFees", !form.enableLateFees)}
              >
                <ToggleInfo>
                  <div className="tg-title">Enable Late Fee Policy</div>
                  <div className="tg-desc">
                    Automatically charge fees for overdue payments
                  </div>
                </ToggleInfo>
                <ToggleSwitch $on={!!form.enableLateFees} />
              </ToggleRow>

              {form.enableLateFees && property?.id ? (
                <LateFeePolicyManager propertyId={property.id} />
              ) : form.enableLateFees ? (
                <InfoNote>
                  Save the property first, then edit it to configure grace period,
                  fee type (percentage, fixed, or daily rate), and maximum caps.
                </InfoNote>
              ) : null}
            </>
          )}

          {/* ── STEP 4 · Utilities ── */}
          {step === 4 && (
            <>
              <SectionLabel>
                <Zap size={12} />
                Utilities Setup
              </SectionLabel>

              <SectionHelper>
                First choose whether metered utilities are shared or not. Then
                configure shared groups or per-unit-type house number mappings.
              </SectionHelper>

              <Field style={{ marginBottom: 12 }}>
                <FieldLbl>Is utility billing shared?</FieldLbl>
                <StyledSelect
                  value={form.utilityBillingMode || "SHARED"}
                  onChange={(e) =>
                    set(
                      "utilityBillingMode",
                      e.target.value as "SHARED" | "INDIVIDUAL",
                    )
                  }
                >
                  <option value="SHARED">
                    Yes, shared utilities (group units)
                  </option>
                  <option value="INDIVIDUAL">
                    No, utilities are not shared (map by unit type)
                  </option>
                </StyledSelect>
              </Field>

              {form.utilityBillingMode === "SHARED" ? (
                <UtilitySetupContainer>
                  {(form.sharedUtilityGroups || []).map((group, idx) => (
                    <UtilityCard key={group.id}>
                      <UtilityCardHead>
                        <UtilityCardTitle>Group #{idx + 1}</UtilityCardTitle>
                        <IconBtn
                          type="button"
                          onClick={() => removeSharedUtilityGroup(group.id)}
                          title="Remove group"
                        >
                          <Trash2 size={14} />
                        </IconBtn>
                      </UtilityCardHead>

                      <TwoCol>
                        <Field>
                          <FieldLbl>Utility Type</FieldLbl>
                          <StyledSelect
                            value={group.utilityType}
                            onChange={(e) =>
                              updateSharedUtilityGroup(group.id, {
                                utilityType: e.target.value,
                              })
                            }
                          >
                            {METERED_UTILITY_TYPE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </StyledSelect>
                        </Field>
                        <Field>
                          <FieldLbl>Group Name</FieldLbl>
                          <Input
                            value={group.groupName}
                            onChange={(e) =>
                              updateSharedUtilityGroup(group.id, {
                                groupName: e.target.value,
                              })
                            }
                            placeholder="e.g. Block A Water Group"
                          />
                        </Field>
                      </TwoCol>
                      <TwoCol>
                        <Field>
                          <FieldLbl>Billing Method</FieldLbl>
                          <StyledSelect
                            value={group.billingMethod}
                            onChange={(e) =>
                              updateSharedUtilityGroup(group.id, {
                                billingMethod: e.target.value as any,
                                ratePerUnit:
                                  e.target.value === "LUMP_SUM_DISTRIBUTION"
                                    ? ""
                                    : group.ratePerUnit,
                              })
                            }
                          >
                            <option value="LUMP_SUM_DISTRIBUTION">
                              Lump Sum (Divide bill)
                            </option>
                            <option value="RATE_PER_UNIT">
                              Rate Per Unit (Reading × Rate)
                            </option>
                          </StyledSelect>
                        </Field>
                        {group.billingMethod === "RATE_PER_UNIT" ? (
                          <Field>
                            <FieldLbl>Rate Per Unit</FieldLbl>
                            <Input
                              type="number"
                              min="0"
                              value={group.ratePerUnit || ""}
                              onChange={(e) =>
                                updateSharedUtilityGroup(group.id, {
                                  ratePerUnit: e.target.value,
                                })
                              }
                              placeholder="e.g. 150"
                            />
                          </Field>
                        ) : (
                          <div />
                        )}
                      </TwoCol>

                      <Field>
                        <FieldLbl>House Numbers in this Group</FieldLbl>
                        <UtilityHouseGrid>
                          {availableUnitNumbers.map((houseNo) => {
                            const selected =
                              group.houseNumbers.includes(houseNo);
                            const usedInSameType = (
                              form.sharedUtilityGroups || []
                            )
                              .filter(
                                (g) =>
                                  g.id !== group.id &&
                                  g.utilityType === group.utilityType,
                              )
                              .some((g) => g.houseNumbers.includes(houseNo));
                            return (
                              <UtilityHouseChip
                                key={`${group.id}-${houseNo}`}
                                type="button"
                                $on={selected}
                                $disabled={!selected && usedInSameType}
                                onClick={() => {
                                  if (!selected && usedInSameType) return;
                                  toggleSharedUtilityHouse(group.id, houseNo);
                                }}
                              >
                                {houseNo}
                              </UtilityHouseChip>
                            );
                          })}
                        </UtilityHouseGrid>
                        <TinyText>
                          Select houses directly. A house cannot belong to more
                          than one shared group for the same utility type.
                        </TinyText>
                      </Field>
                    </UtilityCard>
                  ))}

                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addSharedUtilityGroup}
                    >
                      <Plus size={13} /> Add Shared Group
                    </Button>
                  </div>
                </UtilitySetupContainer>
              ) : (
                <UtilitySetupContainer>
                  {(form.individualUtilityMappings || []).map((map, idx) => (
                    <UtilityCard key={map.id}>
                      <UtilityCardHead>
                        <UtilityCardTitle>Mapping #{idx + 1}</UtilityCardTitle>
                        <IconBtn
                          type="button"
                          onClick={() => removeIndividualUtilityMapping(map.id)}
                          title="Remove mapping"
                        >
                          <Trash2 size={14} />
                        </IconBtn>
                      </UtilityCardHead>

                      <TwoCol>
                        <Field>
                          <FieldLbl>Utility Type</FieldLbl>
                          <StyledSelect
                            value={map.utilityType}
                            onChange={(e) =>
                              updateIndividualUtilityMapping(map.id, {
                                utilityType: e.target.value,
                              })
                            }
                          >
                            {METERED_UTILITY_TYPE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </StyledSelect>
                        </Field>
                        <Field>
                          <FieldLbl>Unit Type</FieldLbl>
                          <StyledSelect
                            value={map.unitType}
                            onChange={(e) =>
                              updateIndividualUtilityMapping(map.id, {
                                unitType: e.target.value,
                              })
                            }
                          >
                            <option value="">Select unit type</option>
                            {(configuredUnitTypes.length
                              ? configuredUnitTypes
                              : UNIT_TYPE_OPTIONS.map((opt) => opt.value)
                            ).map((t) => (
                              <option key={t} value={t}>
                                {getUnitTypeLabel(t)}
                              </option>
                            ))}
                          </StyledSelect>
                        </Field>
                      </TwoCol>
                      <TwoCol>
                        <Field>
                          <FieldLbl>Billing Method</FieldLbl>
                          <StyledSelect
                            value={map.billingMethod}
                            onChange={(e) =>
                              updateIndividualUtilityMapping(map.id, {
                                billingMethod: e.target.value as any,
                                ratePerUnit:
                                  e.target.value === "LUMP_SUM_DISTRIBUTION"
                                    ? ""
                                    : map.ratePerUnit,
                              })
                            }
                          >
                            <option value="LUMP_SUM_DISTRIBUTION">
                              Lump Sum (Divide bill)
                            </option>
                            <option value="RATE_PER_UNIT">
                              Rate Per Unit (Reading × Rate)
                            </option>
                          </StyledSelect>
                        </Field>
                        {map.billingMethod === "RATE_PER_UNIT" ? (
                          <Field>
                            <FieldLbl>Rate Per Unit</FieldLbl>
                            <Input
                              type="number"
                              min="0"
                              value={map.ratePerUnit || ""}
                              onChange={(e) =>
                                updateIndividualUtilityMapping(map.id, {
                                  ratePerUnit: e.target.value,
                                })
                              }
                              placeholder="e.g. 150"
                            />
                          </Field>
                        ) : (
                          <div />
                        )}
                      </TwoCol>

                      <Field>
                        <FieldLbl>
                          House Numbers for{" "}
                          {map.unitType
                            ? getUnitTypeLabel(map.unitType)
                            : "selected type"}
                        </FieldLbl>
                        <UtilityHouseGrid>
                          {availableUnitNumbers
                            .filter((houseNo) => {
                              if (!map.unitType) return true;
                              const owner = (form.unitTypeConfigs || []).find(
                                (cfg) =>
                                  (cfg.houseNumbers || []).includes(houseNo),
                              );
                              return !owner || owner.type === map.unitType;
                            })
                            .map((houseNo) => {
                              const selected =
                                map.houseNumbers.includes(houseNo);
                              return (
                                <UtilityHouseChip
                                  key={`${map.id}-${houseNo}`}
                                  type="button"
                                  $on={selected}
                                  onClick={() =>
                                    toggleIndividualUtilityHouse(
                                      map.id,
                                      houseNo,
                                    )
                                  }
                                >
                                  {houseNo}
                                </UtilityHouseChip>
                              );
                            })}
                        </UtilityHouseGrid>
                      </Field>
                    </UtilityCard>
                  ))}

                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addIndividualUtilityMapping}
                    >
                      <Plus size={13} /> Add Unit Type Mapping
                    </Button>
                  </div>
                </UtilitySetupContainer>
              )}

              <Divider />

              <SectionLabel>
                <DollarSign size={12} />
                Additional Utilities (fixed amount)
              </SectionLabel>

              <SectionHelper>
                Trash, internet, sewerage, security and other are configured
                here with amounts.
              </SectionHelper>

              <AdditionalUtilitiesStack>
                {(form.additionalUtilityCharges || []).map((charge) => (
                  <TwoCol key={charge.type}>
                    <ToggleRow
                      onClick={() =>
                        updateAdditionalUtilityCharge(charge.type, {
                          enabled: !charge.enabled,
                        })
                      }
                    >
                      <ToggleInfo>
                        <div className="tg-title">
                          {
                            ADDITIONAL_UTILITY_OPTIONS.find(
                              (o) => o.value === charge.type,
                            )?.label
                          }
                        </div>
                        <div className="tg-desc">
                          Enable and set fixed amount
                        </div>
                      </ToggleInfo>
                      <ToggleSwitch $on={charge.enabled} />
                    </ToggleRow>

                    <Field>
                      <FieldLbl>Amount (KES)</FieldLbl>
                      <Input
                        type="number"
                        min="0"
                        disabled={!charge.enabled}
                        value={charge.amount}
                        onChange={(e) =>
                          updateAdditionalUtilityCharge(charge.type, {
                            amount: e.target.value,
                          })
                        }
                        placeholder="e.g. 1000"
                      />
                    </Field>
                  </TwoCol>
                ))}
              </AdditionalUtilitiesStack>

              {errors.utilitySetup && (
                <ErrMsg>
                  <AlertCircle size={10} />
                  {errors.utilitySetup}
                </ErrMsg>
              )}
            </>
          )}

          {/* ── STEP 5 · Lease ── */}
          {step === 5 && (
            <>
              <SectionLabel>
                <Edit size={12} />
                Lease Agreement Setup
              </SectionLabel>

              <SectionHelper>
                Required: set the default lease agreement template for this
                property. This follows the same placeholder concept as Lease
                Editor (for example: {"{{tenant_name}}"},
                {"{{landlord_name}}"}, {"{{agreement_date}}"}).
              </SectionHelper>

              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    set("defaultAgreementContent", LEASE_DEFAULT_TEMPLATE)
                  }
                >
                  Use Lease Editor Template
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => set("defaultAgreementContent", "")}
                >
                  Clear
                </Button>
              </div>

              <Field>
                <FieldLbl htmlFor="drw-default-lease-content">
                  Default Lease Agreement Content <span className="req">*</span>
                </FieldLbl>
                <StyledTextarea
                  id="drw-default-lease-content"
                  value={form.defaultAgreementContent || ""}
                  onChange={(e) =>
                    set("defaultAgreementContent", e.target.value)
                  }
                  placeholder="Paste or type your default lease agreement terms here..."
                />
                {errors.defaultAgreementContent && (
                  <ErrMsg>
                    <AlertCircle size={10} />
                    {errors.defaultAgreementContent}
                  </ErrMsg>
                )}
              </Field>
            </>
          )}

          {/* ── STEP 2 · Naming Convention ── */}
          {step === 2 && (
            <>
              <SectionLabel>
                <Settings size={12} />
                Unit Naming Convention
              </SectionLabel>

              <NamingGrid>
                {[
                  {
                    value: "SEQUENTIAL",
                    title: "Sequential",
                    example: "1, 2, 3, 4…",
                  },
                  {
                    value: "FLOOR_UNIT",
                    title: "Floor-Unit",
                    example: "101, 102, 201…",
                  },
                  {
                    value: "BLOCK_UNIT",
                    title: "Block-Unit",
                    example: "A1, A2, B1…",
                  },
                  { value: "CUSTOM", title: "Custom", example: "Your pattern" },
                  {
                    value: "GROUPED_RANGES",
                    title: "Grouped ranges",
                    example: "A1–A14, B1–B13…",
                  },
                ].map((opt) => (
                  <NamingCard
                    key={opt.value}
                    type="button"
                    $active={form.unitNamingConvention === opt.value}
                    onClick={() => {
                      if (opt.value === "GROUPED_RANGES") {
                        setForm((prev) => ({
                          ...prev,
                          unitNamingConvention: "GROUPED_RANGES",
                          unitNamingGroups:
                            prev.unitNamingGroups?.length &&
                            prev.unitNamingGroups.length > 0
                              ? prev.unitNamingGroups
                              : [
                                  {
                                    id: "grp-1",
                                    prefix: "A",
                                    start: "1",
                                    end: "10",
                                  },
                                ],
                        }));
                        setErrors((prev) => {
                          const n = { ...prev };
                          delete n.unitNamingConvention;
                          delete n.unitNamingGroups;
                          return n;
                        });
                      } else {
                        set("unitNamingConvention", opt.value);
                      }
                    }}
                  >
                    <div className="nm-title">{opt.title}</div>
                    <div className="nm-example">{opt.example}</div>
                  </NamingCard>
                ))}
              </NamingGrid>

              {form.unitNamingConvention === "GROUPED_RANGES" && (
                <div
                  style={{
                    marginTop: 12,
                    border: "1px solid var(--border)",
                    borderRadius: 10,
                    padding: 12,
                    display: "grid",
                    gap: 10,
                  }}
                >
                  {(form.unitNamingGroups || []).map((g, idx) => (
                    <div
                      key={g.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(80px, 120px) minmax(90px, 1fr) minmax(90px, 1fr) auto",
                        gap: 8,
                        alignItems: "end",
                      }}
                    >
                      <Field>
                        <FieldLbl>Prefix</FieldLbl>
                        <Input
                          value={g.prefix}
                          onChange={(e) => updateNamingGroup(g.id, { prefix: e.target.value.toUpperCase() })}
                          placeholder="A"
                        />
                      </Field>
                      <Field>
                        <FieldLbl>Start</FieldLbl>
                        <Input
                          type="number"
                          min="1"
                          value={g.start}
                          onChange={(e) => updateNamingGroup(g.id, { start: e.target.value })}
                          placeholder="1"
                        />
                      </Field>
                      <Field>
                        <FieldLbl>End</FieldLbl>
                        <Input
                          type="number"
                          min="1"
                          value={g.end}
                          onChange={(e) => updateNamingGroup(g.id, { end: e.target.value })}
                          placeholder="14"
                        />
                      </Field>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={(form.unitNamingGroups || []).length <= 1}
                        onClick={() => removeNamingGroup(g.id)}
                        title={`Remove group ${idx + 1}`}
                      >
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  ))}
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                      Total generated: {generateGroupedUnitNames().length} / {form.totalUnits}
                    </span>
                    <Button type="button" variant="outline" size="sm" onClick={addNamingGroup}>
                      <Plus size={12} />
                      Add Group
                    </Button>
                  </div>
                  {errors.unitNamingGroups && (
                    <ErrMsg>
                      <AlertCircle size={10} />
                      {errors.unitNamingGroups}
                    </ErrMsg>
                  )}
                </div>
              )}

              {form.unitNamingConvention === "CUSTOM" && (
                <FullCol>
                  <Field>
                    <FieldLbl htmlFor="drw-pattern">
                      Custom Pattern <span className="req">*</span>
                    </FieldLbl>
                    <Input
                      id="drw-pattern"
                      value={form.customNamingPattern || ""}
                      onChange={(e) =>
                        set("customNamingPattern", e.target.value)
                      }
                      placeholder="e.g. {block}-{unit:2} or APT-{floor}-{unit}"
                      error={!!errors.customNamingPattern}
                    />
                    <div
                      style={{
                        fontSize: "0.7rem",
                        color: "var(--muted-foreground)",
                        marginTop: 3,
                      }}
                    >
                      Placeholders: <code>{"{block}"}</code>,{" "}
                      <code>{"{floor}"}</code>, <code>{"{unit}"}</code>,{" "}
                      <code>{"{unit:2}"}</code> (legacy{" "}
                      <code>BLOCK/UNIT/FLOOR/APT</code> also supported)
                    </div>
                    {errors.customNamingPattern && (
                      <ErrMsg>
                        <AlertCircle size={10} />
                        {errors.customNamingPattern}
                      </ErrMsg>
                    )}
                  </Field>
                </FullCol>
              )}

              {form.totalUnits > 0 && (
                <PreviewBox>
                  <PreviewTitle>Preview · sample unit names</PreviewTitle>
                  <PreviewTags>
                    {previewNames().map((n, i) => (
                      <PreviewTag key={i}>{n}</PreviewTag>
                    ))}
                    {form.totalUnits > 6 && (
                      <PreviewMore>+{form.totalUnits - 6} more</PreviewMore>
                    )}
                  </PreviewTags>
                </PreviewBox>
              )}
            </>
          )}
        </Body>

        {/* Footer */}
        <Footer>
          <FooterStep>
            Step <strong>{step}</strong> of <strong>5</strong>
          </FooterStep>
          <FooterBtns>
            {step > 1 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleBack}
                disabled={busy}
              >
                <ChevronLeft size={14} />
                Back
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </Button>
            {step < 5 ? (
              <Button
                size="sm"
                onClick={handleNext}
                disabled={busy || (step === 1 && channelBlocking)}
              >
                Next
                <ChevronRight size={14} />
              </Button>
            ) : (
              <Button
                size="sm"
                loading={busy}
                disabled={busy}
                onClick={handleSubmit}
              >
                {busy ? (
                  <>
                    <Clock size={13} />
                    {property ? " Saving…" : " Creating…"}
                  </>
                ) : (
                  <>
                    {property ? <Edit size={13} /> : <Plus size={13} />}
                    {property ? " Save Changes" : " Create Property"}
                  </>
                )}
              </Button>
            )}
          </FooterBtns>
        </Footer>
      </Drawer>
    </Overlay>
  );
};

export default AddPropertyDrawer;
